import React, { useState } from 'react';
import { TONE, DEFAULT_OPTIONS, SIZE } from './constants';
import { getSelectedIndex } from '../../../utils/arrays';
import clsx from 'clsx';

// Returns tone-based visual classes (track, text, focus, indicator)
const getToneClasses = (toneKey) => TONE[toneKey] ?? TONE.neutral;

/**
 * ViewModeToggle
 * Accessible segmented control component for selecting view modes (e.g. Day / Week / Month).
 *
 * Props:
 * - label: string — visible label before the control (e.g. "View by:")
 * - options: array<{ label: string, value: string }> — options to display
 * - value: string — controlled value (optional)
 * - defaultValue: string — initial value if uncontrolled
 * - onChange: function — callback receiving the new selected value
 * - fullWidth: boolean — if the control should stretch to full width
 * - size: 'sm' | 'md' | 'lg' — button size
 * - tone: 'neutral' | 'primary' | 'success' | 'warning' — visual style
 * - elevated: boolean — applies box-shadow to the control
 * - disabled: boolean — disables the control
 * - id: string — optional ID for accessibility
 * - className: string — additional custom classes
 */
const ViewModeToggle = ({
  label = 'View by:',
  options = DEFAULT_OPTIONS,
  value: controlledValue,
  defaultValue,
  onChange,
  fullWidth = false,
  size = 'md',
  tone = 'neutral',
  elevated = false,
  disabled = false,
  id,
  className,
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? options[0]?.value);
  const currentValue = controlledValue !== undefined ? controlledValue : uncontrolledValue;

  const handleSelection = (newValue) => {
    if (disabled) return;
    if (controlledValue === undefined) setUncontrolledValue(newValue);
    onChange?.(newValue);
  };

  const toneClasses = getToneClasses(tone);
  const currentIndex = getSelectedIndex(options, currentValue);
  const totalOptionCount = options.length;
  const sizeClasses = SIZE[size] ?? SIZE.md;

  // Keyboard navigation actions for accessibility
  const keyboardNavigation = {
    ArrowRight: () => (currentIndex + 1) % totalOptionCount,
    ArrowLeft: () => (currentIndex - 1 + totalOptionCount) % totalOptionCount,
    Home: () => 0,
    End: () => totalOptionCount - 1,
  };

  const handleKeyDown = (event) => {
    const action = keyboardNavigation[event.key];
    if (action) {
      event.preventDefault();
      const newIndex = action();
      handleSelection(options[newIndex].value);
    }
  };

  return (
    <div className={clsx('flex items-center gap-3', fullWidth ? 'w-full' : 'w-auto', className)}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap"
        >
          {label}
        </label>
      )}

      <div
        id={id}
        role="radiogroup"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        className={clsx(
          'relative inline-grid items-stretch overflow-hidden rounded-full transition',
          toneClasses.track,
          elevated && 'shadow-md',
          disabled && 'opacity-60 pointer-events-none',
          fullWidth ? 'w-full' : 'w-auto',
        )}
        style={{ gridTemplateColumns: `repeat(${totalOptionCount}, minmax(0,1fr))` }}
      >
        {/* Sliding animated indicator for selected option */}
        <div
          aria-hidden
          className={clsx(
            'absolute inset-y-0 left-0 rounded-full transition-transform duration-200 ease-out',
            'shadow-inner shadow-white/10',
            toneClasses.indicator,
          )}
          style={{
            width: `calc(100% / ${totalOptionCount})`,
            transform: `translateX(${currentIndex * 100}%)`,
          }}
        />

        {options.map(({ value: optionValue, label: optionLabel }) => {
          const isSelected = currentValue === optionValue;
          return (
            <button
              key={optionValue}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => handleSelection(optionValue)}
              className={clsx(
                'relative z-[1] select-none font-semibold text-center outline-none transition-colors',
                sizeClasses,
                toneClasses.text,
                'focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus-visible:ring-offset-slate-800',
                toneClasses.focus,
              )}
            >
              <span className={isSelected ? 'text-white' : ''}>{optionLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ViewModeToggle;
